"use client";

import { cn } from "@shared/lib/cn";
import { getShikiHighlighter, type ShikiLang } from "@shared/lib/shiki";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

interface ShikiCodeBlockProps {
  code: string;
  lang: ShikiLang;
  className?: string;
}

/**
 * Client-side syntax-highlighted code block using shiki.
 * Falls back to plain <pre> while the highlighter initialises.
 * Background is transparent so the parent card colour shows through.
 */
export const CodeBlock = ({ code, lang, className }: ShikiCodeBlockProps) => {
  const { resolvedTheme } = useTheme();
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getShikiHighlighter().then((hl) => {
      if (cancelled) return;
      const theme = resolvedTheme === "light" ? "github-light" : "github-dark";
      setHtml(hl.codeToHtml(code, { lang, theme }));
    });

    return () => {
      cancelled = true;
    };
  }, [code, lang, resolvedTheme]);

  return (
    <div
      className={cn(
        // Make shiki's inline background-color transparent so the card shows through.
        "[&_pre]:bg-transparent! [&_pre]:m-0 [&_pre]:p-4",
        "[&_pre]:font-mono [&_pre]:text-sm [&_pre]:leading-relaxed",
        !html && "animate-pulse opacity-60",
        className,
      )}
    >
      {html ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <pre className="p-4 font-mono text-sm leading-relaxed text-muted-foreground">{code}</pre>
      )}
    </div>
  );
};
